'use client';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { StatsFiltersProvider, useStatsFilters } from '@/app/analytics/context/StatsFiltersContext';
import { FilterBar } from '@/app/analytics/components/filters/FilterBar';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useReportData, useReportsCatalog } from '@/lib/api/reports';
import { recordReportView } from '@/app/reports/hooks/useReportPreferences';
import { ReportVisualizationView } from '@/app/reports/components/ReportVisualizationView';
import { ReportEmptyState } from '@/app/reports/components/ReportEmptyState';
import { ReportViewerSkeleton } from '@/app/reports/components/ReportViewerSkeleton';

const hasReportData = (result: {
  series: { date: string; value: number }[];
  table?: Record<string, string | number | null>[];
}) => result.series.length > 0 || (result.table?.length ?? 0) > 0;

const reportErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const downloadBlob = (filename: string, content: string, mime: string) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

function ReportViewerContent({ reportId, accountKey }: { reportId: string; accountKey: string }) {
  const { t } = useTranslation('reports');
  const { orgRoutes } = useOrgNavigate();
  const { selectedDates, selectedStores, selectedCurrency, selectedGranularity } = useStatsFilters();
  const { data: catalog = [] } = useReportsCatalog();
  const meta = catalog.find((item: { id: string }) => item.id === reportId);

  const { data, isLoading, isFetching, error } = useReportData(
    reportId,
    {
      startDate: selectedDates.from,
      endDate: selectedDates.to,
      storeIds: selectedStores,
      currency: selectedCurrency,
      granularity: selectedGranularity === 'weekly' ? 'week' : selectedGranularity === 'monthly' ? 'month' : 'day',
    },
    { enabled: Boolean(selectedDates.from && selectedDates.to) },
  );

  useEffect(() => {
    recordReportView(accountKey, reportId);
  }, [accountKey, reportId]);

  const title = meta ? t(meta.titleKey) : reportId;
  const description = meta ? t(meta.descriptionKey) : '';
  const isPercentage =
    reportId.includes('approval') ||
    reportId.includes('conversion') ||
    reportId.includes('success-rate') ||
    reportId.includes('refund-rate') ||
    reportId.includes('decline-rate') ||
    reportId.includes('chargeback-rate') ||
    reportId.includes('risk-decline') ||
    reportId.includes('subscription-orders-share') ||
    reportId.includes('order-bump-take-rate');

  const handleExport = (format: 'csv' | 'json') => {
    if (!data) return;
    const filename = `${reportId}.${format}`;
    if (format === 'json') {
      downloadBlob(filename, JSON.stringify(data, null, 2), 'application/json');
      return;
    }
    const rows = data.series.map((point: { date: string; value: number }) => `${point.date},${point.value}`);
    downloadBlob(filename, `period,value\n${rows.join('\n')}`, 'text/csv');
  };

  const showSkeleton = (isLoading || isFetching) && !data;
  const errorMessage = reportErrorMessage(error, t('viewer.error'));

  return (
    <section className="relative mx-auto max-w-[1920px] pb-12">
      <div className="mb-4 flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to={orgRoutes.REPORTS()}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('viewer.back')}
          </Link>
        </Button>
      </div>

      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h1 className="scroll-mt-8 text-xl font-semibold text-foreground sm:text-2xl">{title}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      <div className="sticky -top-4 z-20 -mx-4 flex flex-col gap-3 border-b border-border bg-background px-4 pb-3 pt-7 sm:flex-row sm:items-center sm:justify-between sm:px-0 sm:pb-3 sm:pt-7 lg:-top-6 lg:pb-4 lg:pt-10">
        <FilterBar />
        <div className="flex shrink-0 items-center gap-2">
          {isFetching && Boolean(data) && (
            <span className="text-xs text-muted-foreground">{t('viewer.refreshing')}</span>
          )}
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')} disabled={!data}>
            <Download className="mr-2 h-4 w-4" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('json')} disabled={!data}>
            <Download className="mr-2 h-4 w-4" />
            JSON
          </Button>
        </div>
      </div>

      <div className="relative mt-6">
        {showSkeleton && <ReportViewerSkeleton />}
        {!showSkeleton && error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
            <p className="text-sm font-medium text-destructive">{t('viewer.error')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{errorMessage}</p>
          </div>
        )}
        {!showSkeleton && !error && data && !hasReportData(data) && (
          <ReportEmptyState variant="page" />
        )}
        {!showSkeleton && !error && data && hasReportData(data) && (() => {
          const plan = meta?.visualizationPlan ?? data.visualizationPlan;
          if (!plan) {
            return <p className="text-sm text-muted-foreground">{t('viewer.error')}</p>;
          }
          return <ReportVisualizationView plan={plan} data={data} isPercentage={isPercentage} />;
        })()}
      </div>
    </section>
  );
}

export default function ReportDetailPage({
  reportId,
  accountKey,
}: {
  reportId: string;
  accountKey: string;
}) {
  return (
    <StatsFiltersProvider defaultDatePreset="lastYear">
      <ReportViewerContent reportId={reportId} accountKey={accountKey} />
    </StatsFiltersProvider>
  );
}
